import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DatePicker } from 'primeng/datepicker';

// Models and services
import { 
  CommissionPlanResponse, 
  CreateCommissionPlanRequest, 
  UpdateCommissionPlanRequest,
  CommissionFrequency,
  OwnershipLevel
} from '../../models';
import { CommissionPlanService } from '../../services';
import { ToastMessageService, CommonService } from '@fitness-force/api';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';

@Component({
  selector: 'lib-commission-plans-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    CardModule,
    SelectModule,
    DividerModule,
    DatePicker,
    BreadcrumbComponent,
    AuditTrailMiniComponent,
],
  template: `
  <div class="md:pt-3 pt-2 md:px-4 px-3 pb-8 mb-4">
    <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
      {{ isEditMode ? ('COMMISSION_MANAGEMENT.PLANS.EDIT' | translate) : ('COMMISSION_MANAGEMENT.PLANS.CREATE' | translate) }}
    </h1>
    <div class="mt-1 mb-3">
      <app-breadcrumb />
    </div>
  
    <div class="grid grid-nogutter">
      <div class="col-12 xl:col-9">
    <div class="card">
      <!-- Header -->
      <div class="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center mb-4 gap-3">
        <div>
          <p class="text-500 mt-0 mb-0">
            {{ isEditMode ? ('COMMISSION_MANAGEMENT.PLANS.EDIT_DESC' | translate) : ('COMMISSION_MANAGEMENT.PLANS.CREATE_DESC' | translate) }}
          </p>
        </div>
        <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.BACK' | translate"
          icon="pi pi-arrow-left"
          [text]="true"
          (onClick)="goBack()"
          aria-label="back-button" />
      </div>
  
      <form [formGroup]="planForm">
        <!-- Ownership Level Selection (Only for Create Mode) -->
        @if (!isEditMode) {
          <div class="grid">
            <div class="col-12">
              <p-card [header]="'COMMISSION_MANAGEMENT.OWNERSHIP.TITLE' | translate" styleClass="mb-4">
                <div class="flex gap-2 flex-wrap">
                  <p-button [label]="'COMMISSION_MANAGEMENT.OWNERSHIP.COMPANY' | translate"
                    [outlined]="selectedOwnershipLevel !== ownershipLevel.Company"
                    [severity]="selectedOwnershipLevel === ownershipLevel.Company ? 'primary' : 'secondary'"
                    (onClick)="selectOwnership(ownershipLevel.Company)"
                    size="small"
                    aria-label="company-level-button" />
                  <p-button [label]="'COMMISSION_MANAGEMENT.OWNERSHIP.FRANCHISE' | translate"
                    [outlined]="selectedOwnershipLevel !== ownershipLevel.FranchiseGroup"
                    [severity]="selectedOwnershipLevel === ownershipLevel.FranchiseGroup ? 'primary' : 'secondary'"
                    (onClick)="selectOwnership(ownershipLevel.FranchiseGroup)"
                    size="small"
                    aria-label="franchise-level-button" />
                  <p-button [label]="'COMMISSION_MANAGEMENT.OWNERSHIP.TENANT' | translate"
                    [outlined]="selectedOwnershipLevel !== ownershipLevel.Tenant"
                    [severity]="selectedOwnershipLevel === ownershipLevel.Tenant ? 'primary' : 'secondary'"
                    (onClick)="selectOwnership(ownershipLevel.Tenant)"
                    size="small"
                    aria-label="tenant-level-button" />
                </div>
              </p-card>
            </div>
          </div>
        }
  
        <!-- Basic Information -->
        <div class="grid">
          <div class="col-12">
            <h4 class="text-lg font-semibold mb-3">{{ 'COMMISSION_MANAGEMENT.PLANS.BASIC_INFO' | translate }}</h4>
          </div>
  
          <!-- Plan Name (Primary Language) -->
          <div class="col-12 md:col-6">
            <label for="name_pri_lang" class="block font-medium mb-2">
              {{ 'COMMISSION_MANAGEMENT.PLANS.NAME_PRIMARY' | translate }}
              <sup class="text-red-500">*</sup>
            </label>
            <input
              pInputText
              id="name_pri_lang"
              formControlName="name_pri_lang"
              class="w-full"
              [placeholder]="'COMMISSION_MANAGEMENT.PLANS.NAME_PRIMARY_PLACEHOLDER' | translate"
              aria-label="plan-name-primary-input" />
              @if (planForm.get('name_pri_lang')?.invalid && planForm.get('name_pri_lang')?.touched) {
                <small class="p-error block mt-1">
                  {{ 'COMMISSION_MANAGEMENT.PLANS.NAME_REQUIRED' | translate }}
                </small>
              }
            </div>
  
            <!-- Plan Name (Secondary Language) -->
            <div class="col-12 md:col-6">
              <label for="name_sec_lang" class="block font-medium mb-2">
                {{ 'COMMISSION_MANAGEMENT.PLANS.NAME_SECONDARY' | translate }}
              </label>
              <input
                pInputText
                id="name_sec_lang"
                formControlName="name_sec_lang"
                class="w-full"
                dir="rtl"
                [placeholder]="'COMMISSION_MANAGEMENT.PLANS.NAME_SECONDARY_PLACEHOLDER' | translate"
                aria-label="plan-name-secondary-input" />
              </div>
  
              <!-- Plan Code -->
              <div class="col-12 md:col-6">
                <label for="code" class="block font-medium mb-2">
                  {{ 'COMMISSION_MANAGEMENT.PLANS.CODE' | translate }}
                  <sup class="text-red-500">*</sup>
                </label>
                <input
                  pInputText
                  id="code"
                  formControlName="code"
                  class="w-full"
                  [disabled]="isEditMode"
                  [placeholder]="'COMMISSION_MANAGEMENT.PLANS.CODE_PLACEHOLDER' | translate"
                  aria-label="plan-code-input" />
                  @if (planForm.get('code')?.invalid && planForm.get('code')?.touched) {
                    <small class="p-error block mt-1">
                      {{ 'COMMISSION_MANAGEMENT.PLANS.CODE_REQUIRED' | translate }}
                    </small>
                  }
                  @if (isEditMode) {
                    <small class="text-500 block mt-1">
                      {{ 'COMMISSION_MANAGEMENT.PLANS.CODE_READONLY' | translate }}
                    </small>
                  }
                </div>
  
                <!-- Frequency -->
                <div class="col-12 md:col-6">
                  <label for="frequency" class="block font-medium mb-2">
                    {{ 'COMMISSION_MANAGEMENT.PLANS.FREQUENCY' | translate }}
                    <sup class="text-red-500">*</sup>
                  </label>
                  <p-select
                    id="frequency"
                    formControlName="frequency"
                    [options]="frequencyOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'COMMISSION_MANAGEMENT.PLANS.SELECT_FREQUENCY' | translate"
                    class="w-full"
                    aria-label="plan-frequency-dropdown">
                    <ng-template let-item pTemplate="item">
                      {{ 'COMMISSION_MANAGEMENT.FREQUENCY.' + item.value.toUpperCase() | translate }}
                    </ng-template>
                    <ng-template let-item pTemplate="selectedItem">
                      {{ 'COMMISSION_MANAGEMENT.FREQUENCY.' + item.value.toUpperCase() | translate }}
                    </ng-template>
                  </p-select>
                  @if (planForm.get('frequency')?.invalid && planForm.get('frequency')?.touched) {
                    <small class="p-error block mt-1">
                      {{ 'COMMISSION_MANAGEMENT.PLANS.FREQUENCY_REQUIRED' | translate }}
                    </small>
                  }
                </div>
  
                <!-- Start Date -->
                <div class="col-12 md:col-6">
                  <label for="start_date" class="block font-medium mb-2">
                    {{ 'COMMISSION_MANAGEMENT.PLANS.START_DATE' | translate }}
                    <sup class="text-red-500">*</sup>
                  </label>
                  <p-datepicker id="start_date"
                    formControlName="start_date"
                    [minDate]="minStartDate"
                    [showIcon]="true"
                    [iconDisplay]="'input'"
                    dateFormat="dd/mm/yy"
                    [placeholder]="'COMMISSION_MANAGEMENT.PLANS.SELECT_START_DATE' | translate"
                    styleClass="w-full"
                    aria-label="plan-start-date-picker" />
                  @if (planForm.get('start_date')?.invalid && planForm.get('start_date')?.touched) {
                    <small class="p-error block mt-1">
                      {{ 'COMMISSION_MANAGEMENT.PLANS.START_DATE_REQUIRED' | translate }}
                    </small>
                  }
                </div>
  
                <!-- End Date -->
                <div class="col-12 md:col-6">
                  <label for="end_date" class="block font-medium mb-2">
                    {{ 'COMMISSION_MANAGEMENT.PLANS.END_DATE' | translate }}
                  </label>
                  <p-datepicker id="end_date"
                    formControlName="end_date"
                    [minDate]="minEndDate"
                    [showIcon]="true"
                    [iconDisplay]="'input'"
                    dateFormat="dd/mm/yy"
                    [placeholder]="'COMMISSION_MANAGEMENT.PLANS.SELECT_END_DATE' | translate"
                    styleClass="w-full"
                    aria-label="plan-end-date-picker" />
                </div>
  
                <!-- Description (Primary Language) -->
                <div class="col-12 md:col-6">
                  <label for="description_pri_lang" class="block font-medium mb-2">
                    {{ 'COMMISSION_MANAGEMENT.PLANS.DESCRIPTION_PRIMARY' | translate }}
                  </label>
                  <textarea
                    pInputTextarea
                    id="description_pri_lang"
                    formControlName="description_pri_lang"
                    rows="3"
                    class="w-full"
                    [placeholder]="'COMMISSION_MANAGEMENT.PLANS.DESCRIPTION_PLACEHOLDER' | translate"
                    aria-label="plan-description-primary-input">
                  </textarea>
                </div>
  
                <!-- Description (Secondary Language) -->
                <div class="col-12 md:col-6">
                  <label for="description_sec_lang" class="block font-medium mb-2">
                    {{ 'COMMISSION_MANAGEMENT.PLANS.DESCRIPTION_SECONDARY' | translate }}
                  </label>
                  <textarea
                    pInputTextarea
                    id="description_sec_lang"
                    formControlName="description_sec_lang"
                    rows="3"
                    class="w-full"
                    dir="rtl"
                    [placeholder]="'COMMISSION_MANAGEMENT.PLANS.DESCRIPTION_PLACEHOLDER' | translate"
                    aria-label="plan-description-secondary-input">
                  </textarea>
                </div>
  
              </div>
  
  
              <!-- Form Actions -->
              <div class="flex justify-content-end gap-2">
                <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.CANCEL' | translate"
                  icon="pi pi-times"
                  [text]="true"
                  severity="secondary"
                  (onClick)="goBack()"
                  aria-label="cancel-button" />
                <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.SAVE' | translate"
                  icon="pi pi-check"
                  [loading]="saving"
                  [disabled]="planForm.invalid"
                  (onClick)="savePlan()"
                  aria-label="save-plan-button" />
              </div>
            </form>
          </div>
        </div>

      @if (showAuditLogsFeature && planId) {
        <div class="hidden xl:block xl:col-3 surface-0 text-sm text-600">
          <fitness-force-audit-trail-mini entityType="commission_plans"
            [entityId]="planId"
            [tenantId]="tenantId"
            [tenantData]="[]"
            [maxEvents]="10"
            [showStats]="true"
            [showFilters]="true"
            [showPagination]="true"
            [showCloseButton]="false"
            height="100vh"
            title="Commission Plan Activity Log"
            data-testid="audit-trail-mini-component" />
        </div>
      }
    </div>
  </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommissionPlansFormComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private commissionPlanService = inject(CommissionPlanService);
  private toastService = inject(ToastMessageService);
  private translate = inject(TranslateService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private commonService = inject(CommonService);
  private store = inject(Store);
  private featureFlagService = inject(FeatureFlagService);

  showAuditLogsFeature = false;

  planForm: FormGroup;
  saving = false;
  planId?: number;
  plan?: CommissionPlanResponse;
  selectedOwnershipLevel: OwnershipLevel = OwnershipLevel.Company;
  tenantId = 0;
  tenantFranchiseId = 0;
  currencyCode = 'USD';
  locale = 'en-US';
  minStartDate: Date = new Date();
  minEndDate: Date = new Date();
  private destroy$ = new Subject<void>();

  frequencyOptions = [
    { label: 'Weekly', value: CommissionFrequency.Weekly },
    { label: 'Fortnightly', value: CommissionFrequency.Fortnightly },
    { label: 'Monthly', value: CommissionFrequency.Monthly },
    { label: 'Quarterly', value: CommissionFrequency.Quarterly }
  ];

  get isEditMode(): boolean {
    return !!this.planId;
  }

  get ownershipLevel() {
    return OwnershipLevel;
  }

  constructor() {
    // Set today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    this.planForm = this.fb.group({
      name_pri_lang: ['', Validators.required],
      name_sec_lang: [''],
      code: ['', Validators.required],
      description_pri_lang: [''],
      description_sec_lang: [''],
      frequency: [CommissionFrequency.Monthly, Validators.required],
      start_date: [today, Validators.required],
      end_date: [null]
    });

    // Watch start_date changes to update minEndDate
    this.planForm.get('start_date')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(startDate => {
        if (startDate) {
          this.minEndDate = new Date(startDate);
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  ngOnInit() {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((showAudit) => {
      this.showAuditLogsFeature = showAudit;
      this.changeDetectorRef.detectChanges();
    });
    // Get currency code and locale from common service
    const currencyInfo = this.commonService.getCurrencyCodeAndLocale();
    this.currencyCode = currencyInfo.currencyCode || 'USD';
    this.locale = currencyInfo.locale || 'en-US';

    // Restore ownership level from service (set when navigating from list)
    this.selectedOwnershipLevel = this.commissionPlanService.getSelectedOwnershipLevel();

    // Load tenant info for ownership-aware API calls, then check route params
    combineLatest([
      this.store.select(TENANTS_INFO).pipe(filter((t: TENANTSINTERFACE) => !!t?.selected_tenant?.id)),
      this.route.params
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([tenant, params]: [TENANTSINTERFACE, Record<string, string>]) => {
          this.tenantId = tenant.selected_tenant.id;
          this.tenantFranchiseId = tenant.selected_tenant.franchise_group_id;

          if (params['id']) {
            const newPlanId = +params['id'];
            if (newPlanId !== this.planId) {
              this.planId = newPlanId;
              // Disable code field in edit mode
              this.planForm.get('code')?.disable();
              this.loadPlan();
            }
          }
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  selectOwnership(ownershipLevel: OwnershipLevel) {
    this.selectedOwnershipLevel = ownershipLevel;
    this.changeDetectorRef.detectChanges();
  }

  loadPlan() {
    if (this.planId) {
      this.commissionPlanService.getCommissionPlanByOwnership(
        this.selectedOwnershipLevel,
        this.planId,
        this.tenantId,
        this.tenantFranchiseId
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (plan: CommissionPlanResponse) => {
            this.plan = plan;
            this.populateForm();
            this.changeDetectorRef.detectChanges();
          },
          error: (error) => {
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
    }
  }

  populateForm() {
    if (this.plan) {
      this.planForm.patchValue({
        name_pri_lang: this.plan.name_pri_lang || '',
        name_sec_lang: this.plan.name_sec_lang || '',
        code: this.plan.code || '',
        description_pri_lang: this.plan.description_pri_lang || '',
        description_sec_lang: this.plan.description_sec_lang || '',
        frequency: this.plan.frequency,
        start_date: this.plan.start_date ? new Date(this.plan.start_date) : null,
        end_date: this.plan.end_date ? new Date(this.plan.end_date) : null
      });
    }
  }

  savePlan() {
    if (this.planForm.valid) {
      this.saving = true;
      const formData = this.planForm.getRawValue();

      // Convert dates to UTC ISO string
      const startDateUTC = formData.start_date ? formData.start_date.toISOString() : undefined;
      const endDateUTC = formData.end_date ? formData.end_date.toISOString() : undefined;

      const request: CreateCommissionPlanRequest | UpdateCommissionPlanRequest = {
        name_pri_lang: formData.name_pri_lang,
        name_sec_lang: formData.name_sec_lang,
        description_pri_lang: formData.description_pri_lang,
        description_sec_lang: formData.description_sec_lang,
        frequency: formData.frequency,
        start_date: startDateUTC,
        end_date: endDateUTC
      };

      // Add code only for create
      if (!this.isEditMode) {
        (request as CreateCommissionPlanRequest).code = formData.code;
      }

      if (this.isEditMode && this.planId) {
        this.commissionPlanService.updateCommissionPlanByOwnership(
          this.selectedOwnershipLevel,
          this.planId,
          request as UpdateCommissionPlanRequest,
          this.tenantId,
          this.tenantFranchiseId
        )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.saving = false;
              this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.PLANS.UPDATE_SUCCESS'));
              this.changeDetectorRef.detectChanges();
              this.goBack();
            },
            error: (error: unknown) => {
              this.saving = false;
              this.toastService.showError(error);
              this.changeDetectorRef.detectChanges();
            }
          });
      } else {
        this.commissionPlanService.createCommissionPlanByOwnership(
          this.selectedOwnershipLevel,
          request as CreateCommissionPlanRequest,
          this.tenantId,
          this.tenantFranchiseId
        )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.saving = false;
              this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.PLANS.CREATE_SUCCESS'));
              this.changeDetectorRef.detectChanges();
              this.goBack();
            },
            error: (error: unknown) => {
              this.saving = false;
              this.toastService.showError(error);
              this.changeDetectorRef.detectChanges();
            }
          });
      }
    }
  }

  goBack() {
    this.router.navigate(['/admin/commission-management/plans']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
